import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  Chip,
  DataGrid,
  Field,
  Input,
  Money,
  PageHeader,
  Toolbar,
  TotalRow,
  addDecimal,
  cmpDecimal,
  formatMoney,
  subDecimal,
  type ColumnDef,
} from '@biovet/ui';
import { useResource, type TrialBalanceResult, type TrialBalanceRow } from '../lib/api.js';
import { exportReport } from '../lib/export.js';
import { printReport, type ReportTable } from '../lib/print.js';
import { baseCurrency, companyToday, monthOf, shiftMonth } from '../lib/reference-data.js';

export function TrialBalanceScreen({ onOpenLedger }: { onOpenLedger: (code: string) => void }) {
  const [range, setRange] = useState(() => monthOf(companyToday()));

  const { data, error, loading } = useResource<TrialBalanceResult>('/trial-balance', {
    from: range.from || undefined,
    to: range.to || undefined,
  });
  const rows = data?.rows ?? [];

  const totals = useMemo(() => {
    let openingDebit = '0';
    let openingCredit = '0';
    let debit = '0';
    let credit = '0';
    let closingDebit = '0';
    let closingCredit = '0';
    for (const row of rows) {
      openingDebit = addDecimal(openingDebit, row.openingDebitBase);
      openingCredit = addDecimal(openingCredit, row.openingCreditBase);
      debit = addDecimal(debit, row.debitBase);
      credit = addDecimal(credit, row.creditBase);
      closingDebit = addDecimal(closingDebit, row.closingDebitBase);
      closingCredit = addDecimal(closingCredit, row.closingCreditBase);
    }
    return {
      openingDebit,
      openingCredit,
      debit,
      credit,
      closingDebit,
      closingCredit,
      difference: subDecimal(debit, credit),
      closingDifference: subDecimal(closingDebit, closingCredit),
    };
  }, [rows]);

  const balanced = cmpDecimal(totals.difference, '0') === 0;
  const closingBalanced = cmpDecimal(totals.closingDifference, '0') === 0;

  const columns = useMemo<ColumnDef<TrialBalanceRow, unknown>[]>(
    () => [
      {
        id: 'code',
        header: 'Code',
        accessorKey: 'code',
        cell: (c) => (
          <button
            type="button"
            onClick={() => onOpenLedger(c.getValue<string>())}
            className="bb-mono cursor-pointer border-0 bg-transparent p-0 text-[12px] text-[var(--accent)] hover:underline"
          >
            {c.getValue<string>()}
          </button>
        ),
        meta: { width: '96px' },
      },
      { id: 'name', header: 'Account', accessorKey: 'name' },
      {
        id: 'type',
        header: 'Type',
        accessorKey: 'type',
        cell: (c) => (
          <span className="text-[var(--ink-3)]">{c.getValue<string>().toLowerCase()}</span>
        ),
        meta: { width: '104px' },
      },
      {
        id: 'opening',
        header: 'Opening',
        accessorKey: 'openingDebitBase',
        cell: (c) => (
          <SidedBalance
            debit={c.row.original.openingDebitBase}
            credit={c.row.original.openingCreditBase}
          />
        ),
        meta: { align: 'right', numeric: true, width: '150px' },
      },
      {
        id: 'debit',
        header: 'Debit',
        accessorKey: 'debitBase',
        cell: (c) => <Money value={c.getValue<string>()} dashOnZero />,
        meta: { align: 'right', numeric: true, width: '150px' },
      },
      {
        id: 'credit',
        header: 'Credit',
        accessorKey: 'creditBase',
        cell: (c) => <Money value={c.getValue<string>()} dashOnZero />,
        meta: { align: 'right', numeric: true, width: '150px' },
      },
      {
        id: 'closing',
        header: 'Closing',
        accessorKey: 'closingDebitBase',
        cell: (c) => (
          <SidedBalance
            debit={c.row.original.closingDebitBase}
            credit={c.row.original.closingCreditBase}
          />
        ),
        meta: { align: 'right', numeric: true, width: '164px' },
      },
    ],
    [onOpenLedger],
  );

  const report = (): ReportTable => ({
    title: 'Trial balance',
    fileName: 'trial-balance',
    meta: `${data?.from ?? range.from} to ${data?.to ?? range.to} · ${baseCurrency()} · ${rows.length} accounts · ${
      balanced ? 'nets to zero' : `out by ${formatMoney(totals.difference)}`
    }`,
    columns: [
      { label: 'Code', width: '84px' },
      { label: 'Account' },
      { label: 'Type', width: '84px' },
      { label: 'Opening', align: 'right', width: '112px' },
      { label: 'Debit', align: 'right', width: '104px' },
      { label: 'Credit', align: 'right', width: '104px' },
      { label: 'Closing', align: 'right', width: '112px' },
    ],
    rows: [
      ...rows.map((row) => [
        row.code,
        row.name,
        row.type.toLowerCase(),
        sidedText(row.openingDebitBase, row.openingCreditBase),
        formatMoney(row.debitBase, { dashOnZero: true }),
        formatMoney(row.creditBase, { dashOnZero: true }),
        sidedText(row.closingDebitBase, row.closingCreditBase),
      ]),
      {
        cells: [
          '',
          'Total',
          '',
          sidedText(totals.openingDebit, totals.openingCredit),
          formatMoney(totals.debit),
          formatMoney(totals.credit),
          sidedText(totals.closingDebit, totals.closingCredit),
        ],
        total: true,
      },
    ],
    notes: balanced ? undefined : [`Debits exceed credits by ${formatMoney(totals.difference)}.`],
  });

  return (
    <>
      <PageHeader
        breadcrumb="Finance & Accounts / Trial Balance"
        title="Trial balance"
        description="Opening, movement and closing balance for every account in the period."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => printReport(report())}>
              Print
            </Button>
            <Button variant="secondary" size="sm" onClick={() => exportReport(report())}>
              Export XLSX
            </Button>
          </>
        }
        meta={
          <>
            <Chip>
              {data?.from ?? range.from} → {data?.to ?? range.to}
            </Chip>
            <Chip>PKR</Chip>
            <Chip>{rows.length} accounts</Chip>
            {!loading &&
              rows.length > 0 &&
              (balanced ? (
                <Badge tone="ok" dot>
                  Nets to zero
                </Badge>
              ) : (
                <Badge tone="bad">Out by {formatMoney(totals.difference)}</Badge>
              ))}
          </>
        }
      />

      {error && (
        <div className="pb-3.5">
          <Callout tone="danger" title="Could not load the trial balance">
            {error}
          </Callout>
        </div>
      )}

      {!loading && rows.length > 0 && !balanced && (
        <div className="pb-3.5">
          <Callout tone="danger" title="The trial balance does not net to zero">
            Debits exceed credits by {formatMoney(totals.difference)}. The balance invariant is a
            deferred constraint trigger on <code className="bb-mono">vouchers</code>, so this should
            be impossible — it means something reached{' '}
            <code className="bb-mono">voucher_lines</code> without going through a voucher.
          </Callout>
        </div>
      )}

      <Card flush className="mb-3.5" data-noprint="1">
        <Toolbar>
          <Field label="From">
            <Input
              size="sm"
              type="date"
              value={range.from}
              aria-label="From date"
              onChange={(e) => setRange({ ...range, from: e.target.value })}
            />
          </Field>
          <Field label="To">
            <Input
              size="sm"
              type="date"
              value={range.to}
              aria-label="To date"
              onChange={(e) => setRange({ ...range, to: e.target.value })}
            />
          </Field>
          <Button size="sm" variant="ghost" onClick={() => setRange(monthOf(companyToday()))}>
            This month
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setRange(shiftMonth(range.from, -1))}
            title="The month before the one shown"
          >
            Previous month
          </Button>
          <div className="flex-1" />
          <span className="text-[11.5px] text-[var(--ink-3)]">
            Opening is everything posted before {data?.from ?? range.from}.
          </span>
        </Toolbar>
      </Card>

      <Card flush>
        <DataGrid
          aria-label="Trial balance"
          data={rows}
          columns={columns}
          loading={loading}
          emptyTitle="No movement yet"
          emptyDescription="No account has an opening balance or any movement in this window."
          footer={
            <>
              <TotalRow
                cells={[
                  { content: '', width: '96px' },
                  { content: 'Total movement' },
                  { content: '', width: '104px' },
                  {
                    content: <Money value={totals.openingDebit} dashOnZero />,
                    align: 'right',
                    numeric: true,
                    width: '150px',
                  },
                  {
                    content: <Money value={totals.debit} />,
                    align: 'right',
                    numeric: true,
                    width: '150px',
                  },
                  {
                    content: <Money value={totals.credit} />,
                    align: 'right',
                    numeric: true,
                    width: '150px',
                  },
                  {
                    content: balanced ? (
                      <span className="text-[var(--credit)]">in balance</span>
                    ) : (
                      <Money value={totals.difference} colorBySign />
                    ),
                    align: 'right',
                    width: '164px',
                  },
                ]}
              />
              <TotalRow
                cells={[
                  { content: '', width: '96px' },
                  { content: 'Closing balances' },
                  { content: '', width: '104px' },
                  { content: '', width: '150px' },
                  {
                    content: <Money value={totals.closingDebit} />,
                    align: 'right',
                    numeric: true,
                    width: '150px',
                  },
                  {
                    content: <Money value={totals.closingCredit} />,
                    align: 'right',
                    numeric: true,
                    width: '150px',
                  },
                  {
                    content: closingBalanced ? (
                      <span className="text-[var(--credit)]">in balance</span>
                    ) : (
                      <Money value={totals.closingDifference} colorBySign />
                    ),
                    align: 'right',
                    width: '164px',
                  },
                ]}
              />
            </>
          }
        />
      </Card>

      <p data-report-foot="1" className="hidden pt-4 text-[11px] text-[var(--ink-3)]">
        System-generated from biovet Accounting. Figures in PKR.
      </p>
    </>
  );
}

function sidedText(debit: string, credit: string): string {
  const isDebit = cmpDecimal(debit, '0') !== 0;
  const value = isDebit ? debit : credit;
  if (cmpDecimal(value, '0') === 0) return '—';
  return `${formatMoney(value)} ${isDebit ? 'Dr' : 'Cr'}`;
}

function SidedBalance({ debit, credit }: { debit: string; credit: string }) {
  const isDebit = cmpDecimal(debit, '0') !== 0;
  const value = isDebit ? debit : credit;
  if (cmpDecimal(value, '0') === 0) return <span className="text-[var(--ink-3)]">—</span>;
  return (
    <span className="bb-num whitespace-nowrap">
      {formatMoney(value)}
      <span
        className={`pl-1 text-[0.85em] ${isDebit ? 'text-[var(--debit)]' : 'text-[var(--credit)]'}`}
      >
        {isDebit ? 'Dr' : 'Cr'}
      </span>
    </span>
  );
}

